/**
 * R99 · Bulk-Reassign Designation Dialog
 * ---------------------------------------------------------------
 * Admin action on a package: pick a designation used across the
 * package's skill breakdowns, then swap every matching row's
 * assignee to a new person in one click. Useful when a team member
 * leaves or a project changes owners.
 *
 * Props:
 *   open, onClose
 *   lines            — current package lines (kept in sync via prop)
 *   members          — [{user_id, name, email}]
 *   designations     — []
 *   onApply(patches) — receives [{index, skill_breakdown}]; parent
 *                      merges them into local state + saves.
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Users, Shuffle, AlertTriangle } from "lucide-react";

const UNASSIGNED = "__unassigned__";

export default function BulkReassignDialog({ open, onClose, lines, members, designations, onApply }) {
  const [designation, setDesignation] = useState("");
  const [scope, setScope] = useState("all"); // "all" | user_id | UNASSIGNED
  const [newAssignee, setNewAssignee] = useState(""); // user_id | UNASSIGNED

  // Aggregate skill rows so we can surface counts + validate before apply.
  const analysis = useMemo(() => {
    const byDesig = new Map(); // designation → [{lineIdx, skillIdx, assignee_id}]
    (lines || []).forEach((ln, li) => {
      (ln.skill_breakdown || []).forEach((sk, si) => {
        const d = (sk?.designation || "").trim();
        if (!d) return;
        if (!byDesig.has(d)) byDesig.set(d, []);
        byDesig.get(d).push({ li, si, assignee_id: sk.assignee_id || null });
      });
    });
    const designationsInUse = Array.from(byDesig.keys()).sort();
    const rows = designation ? (byDesig.get(designation) || []) : [];
    const buckets = new Map(); // assignee_id | UNASSIGNED → count
    rows.forEach((r) => {
      const key = r.assignee_id || UNASSIGNED;
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });
    return { byDesig, designationsInUse, rows, buckets };
  }, [lines, designation]);

  const memberById = useMemo(() => {
    const m = new Map();
    (members || []).forEach((mem) => { m.set(mem.user_id, mem); });
    return m;
  }, [members]);
  const nameFor = (uid) => (uid === UNASSIGNED
    ? "— Unassigned —"
    : (memberById.get(uid)?.name || memberById.get(uid)?.email || uid));

  const affectedRows = analysis.rows.filter((r) => {
    if (scope === "all") return true;
    if (scope === UNASSIGNED) return !r.assignee_id;
    return r.assignee_id === scope;
  });

  const submit = () => {
    if (!designation || !newAssignee) return;
    const newVal = newAssignee === UNASSIGNED ? null : newAssignee;
    // Skip no-ops.
    const targets = affectedRows.filter((r) => (r.assignee_id || null) !== newVal);
    if (targets.length === 0) {
      onClose?.();
      return;
    }
    // Group by line so we submit one patch per line.
    const byLine = new Map();
    targets.forEach((r) => {
      if (!byLine.has(r.li)) byLine.set(r.li, new Set());
      byLine.get(r.li).add(r.si);
    });
    const patches = [];
    for (const [li, siSet] of byLine.entries()) {
      const ln = lines[li];
      const nextSkb = (ln.skill_breakdown || []).map((sk, si) => (
        siSet.has(si) ? { ...sk, assignee_id: newVal } : sk
      ));
      patches.push({ index: li, skill_breakdown: nextSkb });
    }
    onApply?.(patches);
    onClose?.();
  };

  const ready = !!designation && !!newAssignee && affectedRows.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="pkg-bulk-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Shuffle className="w-4 h-4" /> Bulk reassign designation
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            Swap the assignee on every skill row that uses a given designation across this package. Useful when a
            team member leaves or a project changes owners.
          </p>

          {/* Step 1 · Pick designation */}
          <div>
            <Label className="text-xs font-semibold">Designation</Label>
            <Select value={designation || "_pick"} onValueChange={(v) => { setDesignation(v === "_pick" ? "" : v); setScope("all"); setNewAssignee(""); }}>
              <SelectTrigger className="h-9 rounded-none" data-testid="pkg-bulk-reassign-designation">
                <SelectValue placeholder="Choose a designation…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_pick">Choose a designation…</SelectItem>
                {analysis.designationsInUse.length === 0 && (
                  <SelectItem value="_empty" disabled>No designations found in this package</SelectItem>
                )}
                {analysis.designationsInUse.map((d) => {
                  const total = (analysis.byDesig.get(d) || []).length;
                  return (
                    <SelectItem key={d} value={d} data-testid={`pkg-bulk-reassign-desig-${d}`}>
                      {d} · {total} row{total === 1 ? "" : "s"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Step 2 · Show breakdown + scope selector */}
          {designation && (
            <div className="border border-border p-3 space-y-2 bg-muted/20" data-testid="pkg-bulk-reassign-breakdown">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Current assignments for “{designation}” · {analysis.rows.length} row{analysis.rows.length === 1 ? "" : "s"}
              </div>
              <ul className="text-xs space-y-1">
                {[...analysis.buckets.entries()].map(([uid, cnt]) => (
                  <li key={uid} className="flex items-center gap-2">
                    <Users className={`w-3 h-3 ${uid === UNASSIGNED ? "text-muted-foreground" : "text-primary"}`} />
                    <span className="truncate">{nameFor(uid)}</span>
                    <span className="ml-auto text-muted-foreground">{cnt} row{cnt === 1 ? "" : "s"}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-2 border-t border-border">
                <Label className="text-xs font-semibold">Scope</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="h-9 rounded-none" data-testid="pkg-bulk-reassign-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All rows with this designation ({analysis.rows.length})</SelectItem>
                    {[...analysis.buckets.entries()].map(([uid, cnt]) => (
                      <SelectItem key={uid} value={uid} data-testid={`pkg-bulk-reassign-scope-${uid}`}>
                        Only rows assigned to {nameFor(uid)} ({cnt})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 3 · Pick new assignee */}
          {designation && (
            <div>
              <Label className="text-xs font-semibold">New assignee</Label>
              <Select value={newAssignee || "_pick"} onValueChange={(v) => setNewAssignee(v === "_pick" ? "" : v)}>
                <SelectTrigger className="h-9 rounded-none" data-testid="pkg-bulk-reassign-new-assignee">
                  <SelectValue placeholder="Assign to…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_pick">Assign to…</SelectItem>
                  <SelectItem value={UNASSIGNED} data-testid="pkg-bulk-reassign-new-unassigned">— Unassigned —</SelectItem>
                  {(members || []).map((mem) => (
                    <SelectItem key={mem.user_id} value={mem.user_id} data-testid={`pkg-bulk-reassign-new-${mem.user_id}`}>
                      {mem.name || mem.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Preview + guardrails */}
          {designation && newAssignee && (
            <div className="text-xs border border-border bg-background p-2" data-testid="pkg-bulk-reassign-preview">
              {affectedRows.length === 0 ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Nothing to change — every row in scope already matches the target assignee.
                </div>
              ) : (
                <div>
                  Will reassign <b>{affectedRows.length}</b> skill row{affectedRows.length === 1 ? "" : "s"}{" "}
                  {(scope === "all") ? "" : (
                    <>currently on <b>{nameFor(scope)}</b> </>
                  )}
                  to <b>{nameFor(newAssignee)}</b>.
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    Effort hours are untouched. Save the package after applying to persist.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={!ready} data-testid="pkg-bulk-reassign-apply">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
